import React, { useState, useEffect, useCallback } from 'react';
import { Clock } from 'lucide-react';
import CustomerCard from '../customer/CustomerCard';
import useCustomerActions from '../../hooks/useCustomerActions';
import { apiFetch } from '../../utils/apiClient';

const RecentActivity = ({ storeInfo }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    stampRules,
    fetchStampRules,
    actioningPhone,
    addStamp,
    fetchCoupons,
    fetchCustomer,
    redeemCoupon,
  } = useCustomerActions(storeInfo);

  const fetchRecentCustomers = useCallback(async () => {
    if (!storeInfo?.id) return;
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(
        `/api/customers/recent?store_id=${encodeURIComponent(storeInfo.id)}&limit=15`
      );
      const result = await response.json();
      if (result.success) {
        setCustomers(result.data || []);
      } else {
        setError(result.message || '최근 방문을 불러오지 못했습니다.');
      }
    } catch (err) {
      setError('네트워크 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [storeInfo]);

  useEffect(() => {
    fetchRecentCustomers();
    fetchStampRules();
  }, [fetchRecentCustomers, fetchStampRules]);

  // 목록을 통째로 다시 불러오면 정렬 순서상 방금 처리한 고객이 맨 위로 튀어서
  // 연속으로 여러 명을 처리하기 불편해진다. 이미 목록에 있는 고객이면
  // 그 고객 정보만 갱신해서 자리를 유지하고, 목록에 없던 고객일 때만 전체를 다시 불러온다.
  const refreshAfterAction = async (phone) => {
    const alreadyInList = customers.some((c) => c.phone === phone);
    if (!alreadyInList) {
      await fetchRecentCustomers();
      return;
    }
    try {
      const updated = await fetchCustomer(phone);
      setCustomers((prev) => prev.map((c) => (c.phone === phone ? updated : c)));
    } catch (err) {
      // 단건 갱신 실패 시 조용히 무시(다음 새로고침 때 맞춰짐)
    }
  };

  const handleStampAdd = async (phone) => {
    setError('');
    try {
      await addStamp(phone);
      await refreshAfterAction(phone);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCouponUse = async (phone, couponNumber) => {
    setError('');
    try {
      await redeemCoupon(phone, couponNumber);
      await refreshAfterAction(phone);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-3">
      <h3 className="text-lg font-semibold mb-4 flex items-center">
        <Clock className="w-5 h-5 mr-2 text-gray-600" />
        최근 방문
      </h3>

      {error && (
        <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3 mb-3">{error}</div>
      )}

      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-6 text-gray-500 text-sm">불러오는 중...</div>
        ) : customers.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm">최근 방문이 없습니다.</div>
        ) : (
          customers.map((customer) => (
            <CustomerCard
              key={customer.phone}
              customer={customer}
              stampRules={stampRules}
              isBusy={actioningPhone === customer.phone}
              onStampAdd={handleStampAdd}
              onFetchCoupons={fetchCoupons}
              onCouponUse={handleCouponUse}
              compact
              hideActions
            />
          ))
        )}
      </div>
    </div>
  );
};

export default RecentActivity;
